import { getChangeOwnershipStatus } from '../services/OwnershipChangeService';
import { useChangeOwnershipContext } from '../context/change-ownership-context/ChangeOwnershipContext';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeOwnershipStatus } from '../models/OwnershipChange';
import { useNavigate } from 'react-router';
import { getDebt } from '../services/BillService';
import { getHasPendingOrders } from '../services/OrderService';
import { getCustomerData } from '../services/CustomerService';
import { useEscalation } from './useEscalation';

export const CHANGE_OWNERSHIP_QUERY_KEY = ['change-ownership-status'];

const getChangeOwnershipStatusData = async (): Promise<ChangeOwnershipStatus> => {
    const response = await getChangeOwnershipStatus();
    return response.data;
};

type FlowType = 'isReceptor' | 'isDonante';

const useChangeOwnership = () => {
    const navigate = useNavigate();
    const { setDataChangeOwnership, resetData } = useChangeOwnershipContext();
    const { handleEscalationFlow, escalationStorageListener } = useEscalation();
    const queryClient = useQueryClient();
    const changeOwnershipFlow = localStorage.getItem('changeOwnershipFlow');

    const getChangeOwnership = async () => {
        try {
            const data = await queryClient.fetchQuery({
                queryKey: CHANGE_OWNERSHIP_QUERY_KEY,
                queryFn: getChangeOwnershipStatusData,
                staleTime: 300000,
            });

            setDataChangeOwnership(data);
            return data;
        } catch (error) {
            console.error(error);
            setDataChangeOwnership(null);
            return null;
        }
    };

    const initializeChangeOwnership = async () => {
        resetData();
        localStorage.removeItem('obd');
        localStorage.removeItem('escalation');
        const data = await getChangeOwnership();
        if (!data) return;

        const flowType = await resolveFlowType(data);
        await executeChangeOwnershipFlow(data, flowType);
    };

    const resolveFlowType = async (data: ChangeOwnershipStatus): Promise<FlowType | undefined> => {
        try {
            const customerData = await queryClient.fetchQuery({
                queryKey: ['customer-data'],
                queryFn: getCustomerData,
                staleTime: 300000,
            });
            const customerId = customerData?.data?.id;

            if (!customerId) return;

            const isReceptor = !!data?.interested?.interestedId && customerId === data?.interested?.interestedId;
            const isDonante =
                !data?.interested?.interestedId ||
                (data?.interested?.interestedId && customerId !== data?.interested?.interestedId);

            if (isDonante) return 'isDonante';
            if (isReceptor) return 'isReceptor';
        } catch (error) {
            console.error(error);
        }

        return;
    };

    const getCustomerDebt = async () => {
        try {
            const response = await getDebt();
            return response.data as boolean;
        } catch (error) {
            console.error(error);
        }
    };

    const getPendingOrders = async () => {
        try {
            const response = await getHasPendingOrders();
            return response.data.hasPendingOrders as boolean;
        } catch (error) {
            console.error(error);
        }
    };

    const executeChangeOwnershipFlow = async (dataChangeOwnership: ChangeOwnershipStatus, flowType?: FlowType) => {
        const requiresFlowType = ['INPROGRESS', 'CONTINUE'].includes(dataChangeOwnership?.status || '');
        if (requiresFlowType && !flowType) return;

        switch (dataChangeOwnership?.status) {
            case 'INITIATE':
                await handleInitiateCase();
                break;
            case 'CANCEL':
                navigate('/change-ownership/cancel');
                break;
            case 'INPROGRESS':
                navigate('/change-ownership/feedback?status=SIGNED');
                break;
            case 'CONTINUE':
                await handleContinueCase(flowType);
                break;
        }
    };

    const handleInitiateCase = async () => {
        const hasDebt = await getCustomerDebt();
        if (hasDebt) {
            navigate('/change-ownership/feedback?status=FAILED_PETITIONER');
            return;
        }

        const hasPendingOrders = await getPendingOrders();
        if (hasPendingOrders) {
            navigate('/change-ownership/feedback?status=FAILED_PETITIONER');
            return;
        }

        await handleEscalationFlow('/change-ownership/product-selection', 'initiateChangeOwnership', true);
        escalationStorageListener('/change-ownership/product-selection');
    };

    const handleContinueCase = async (flowType?: FlowType) => {
        if (flowType === 'isDonante') {
            navigate('/change-ownership/feedback?status=SIGNED');
            return;
        }

        if (flowType === 'isReceptor') {
            if (changeOwnershipFlow === 'notNewCustomer') {
                const hasDebt = await getCustomerDebt();
                if (hasDebt) {
                    navigate('/change-ownership/feedback?status=FAILED_INTERESTED');
                    return;
                }

                const hasPendingOrders = await getPendingOrders();
                if (hasPendingOrders) {
                    navigate('/change-ownership/feedback?status=FAILED_INTERESTED');
                    return;
                }

                await handleEscalationFlow('/change-ownership/customer-accounts', 'continueChangeOwnership');
                escalationStorageListener('/change-ownership/customer-accounts');
            }

            if (changeOwnershipFlow === 'newCustomer') {
                navigate('/change-ownership/new-user');
                return;
            }
        }
    };

    return { getChangeOwnership, initializeChangeOwnership };
};

export default useChangeOwnership;
